<app-dialog [size]="DialogSize.LARGE" [dataSelector]="dataSelector + '-dialog'">

  <!-- Dialog Header -->
  <app-dialog-header [dataSelector]="dataSelector + '-dialog-header'">Assign to Someone Else</app-dialog-header>

  <span class="body-300" [dataSelector]="dataSelector + '-description'">When you assign a request to someone else, they'll receive a notification. All existing assignees remain the same.</span>

  @if (isLoadingGrants) {
    <div [dataSelector]="dataSelector + '-loading-grants'">
      <span class="small-400">Loading permissions for this email, please wait...</span>
    </div>
  }

  <div class="reassign-modal-content" [dataSelector]="dataSelector + '-content'">
    <div class="navigation-side" [dataSelector]="dataSelector + '-navigation'">
      <span class="request-name h4-400" [dataSelector]="dataSelector + '-request-name'">{{ requestName() }}</span>
      <hr class="separator" [dataSelector]="dataSelector + '-separator'" />
      @for(section of sections$ | async; track section.data.id) {
        <div class="section-item" (click)="selectSection(section)" [dataSelector]="dataSelector + '-section-' + section.data.id">
          <aiwyn-icon [name]="expandedSections[section.data.id] ? 'chevron-down' : 'chevron-right'" [width]="20" [height]="20" (click)="expandSection(section.data)" [dataSelector]="dataSelector + '-section-toggle-' + section.data.id"></aiwyn-icon>

          <mat-checkbox
            [disabled]="isLoadingGrants || !isSectionSelectable(section)"
            [checked]="section.selected"
            [indeterminate]="section.indeterminate"
            class="d-flex"
            (click)="selectSection(section, $event)"
            [dataSelector]="dataSelector + '-section-checkbox-' + section.data.id"
          ></mat-checkbox>

          <span class="h4-400 color-gray-200" [dataSelector]="dataSelector + '-section-name-' + section.data.id">{{ section.data.name }}</span>
        </div>

        @if(expandedSections[section.data.id]) {
          <div class="request-items-list" [dataSelector]="dataSelector + '-section-items-' + section.data.id">
            @for(requestItem of section.data.items; track requestItem.data.id) {
              <div class="request-item" (click)="selectItem(requestItem)" [dataSelector]="dataSelector + '-request-item-' + requestItem.data.id">
                <mat-checkbox
                  [disabled]="isLoadingGrants || !isSelectableItemForSection(requestItem)"
                  [checked]="requestItem.selected"
                  class="d-flex"
                  (click)="selectItem(requestItem, $event)"
                  [dataSelector]="dataSelector + '-request-item-checkbox-' + requestItem.data.id"
                ></mat-checkbox>

                <span class="h4-400 color-gray-200" [dataSelector]="dataSelector + '-request-item-name-' + requestItem.data.id">{{requestItem.data.name}}</span>
              </div>
            }
          </div>
        }
      }
    </div>

    <form [formGroup]="assigneeForm" class="row assignee-form-side" [dataSelector]="dataSelector + '-form'">
      <div class="form-control col-6" [dataSelector]="dataSelector + '-first-name-field'">
        <label [dataSelector]="dataSelector + '-first-name-label'">First Name</label>
        <input
          formControlName="fname"
          name="reassignment-fname"
          class="form-input mt-2"
          [class]="{ 'form-input--invalid': assigneeForm.controls.fname.touched && assigneeForm.controls.fname.errors }"
          [dataSelector]="dataSelector + '-first-name-input'"
        />

        @if(assigneeForm.controls.fname.touched && assigneeForm.controls.fname.errors?.required) {
          <mat-error  class="d-block small-300 color-red-200 mt-2" [dataSelector]="dataSelector + '-first-name-error'">
            First Name is required.
          </mat-error>
        }
      </div>

      <div class="form-control col-6" [dataSelector]="dataSelector + '-last-name-field'">
        <label [dataSelector]="dataSelector + '-last-name-label'">Last Name</label>
        <input
          formControlName="lname"
          name="reassignment-lname"
          class="form-input mt-2"
          [class]="{ 'form-input--invalid': assigneeForm.controls.lname.touched && assigneeForm.controls.lname.errors }"
          [dataSelector]="dataSelector + '-last-name-input'"
        />

        @if(assigneeForm.controls.lname.touched && assigneeForm.controls.lname.errors?.required) {
          <mat-error class="d-block small-300 color-red-200 mt-2" [dataSelector]="dataSelector + '-last-name-error'">
            Last Name is required.
          </mat-error>
        }
      </div>

      <div class="form-control col-12 mt-4" [dataSelector]="dataSelector + '-email-field'">
        <label [dataSelector]="dataSelector + '-email-label'">Email</label>

        <input
          cdkOverlayOrigin
          #emailSearchEl="cdkOverlayOrigin"
          name="reassignment-email"
          class="email-search-field form-input mt-2"
          [class]="{
            'form-input--invalid': (emailSearch.touched && emailSearch.errors) || (assigneeForm.controls.email.touched && assigneeForm.controls.email.errors)
          }"
          [formControl]="emailSearch"
          [placeholder]="assigneeForm.controls.email.value"
          (focusin)="setOpenState(true)"
          (change)="onChangeEmailField()"
          (keydown.enter)="confirmNewValue()"
          (keydown.tab)="confirmNewValue()"
          (keydown.escape)="cancelNewValue()"
          [dataSelector]="dataSelector + '-email-input'"
        />

        @if(emailSearch.touched && emailSearch.errors?.email) {
          <mat-error class="d-block small-300 color-red-200 mt-2" [dataSelector]="dataSelector + '-email-invalid-error'">
            Invalid email address.
          </mat-error>
        } @else if(assigneeForm.controls.email.touched && assigneeForm.controls.email.errors?.required) {
          <mat-error class="d-block small-300 color-red-200 mt-2" [dataSelector]="dataSelector + '-email-required-error'">
            Email is required.
          </mat-error>
        }


        <ng-template
          cdkConnectedOverlay
          [cdkConnectedOverlayWidth]="triggerWidth ?? 0"
          [cdkConnectedOverlayMinWidth]="triggerWidth ?? 0"
          [cdkConnectedOverlayOrigin]="emailSearchEl"
          [cdkConnectedOverlayOpen]="isOpen"
          (overlayOutsideClick)="onClickOutside($event)"
          (detach)="setOpenState(false)"
        >
          <div class="cxp-select-panel" [dataSelector]="dataSelector + '-email-dropdown'">
            @for(email of availableEmails$ | async; track email; let i = $index) {
              <div
                class="email-option"
                [class]="{
                  'email-option-selected': email === assigneeForm.controls.email.value
                }"
                (click)="confirmNewValue(email)"
                [dataSelector]="dataSelector + '-email-option-' + i"
              >
                @if(email === assigneeForm.controls.email.value) {
                  <aiwyn-icon name="check" [height]="16" [width]="16" aria-hidden="true" [dataSelector]="dataSelector + '-email-selected-icon-' + i"></aiwyn-icon>
                }
                <span [dataSelector]="dataSelector + '-email-option-text-' + i">{{ email }}</span>
              </div>
            }
          </div>
        </ng-template>
      </div>

      <div class="form-control col-12 mt-4" [dataSelector]="dataSelector + '-message-field'">
        <label [dataSelector]="dataSelector + '-message-label'" for="reassignment-message">Message (optional)</label>
        <textarea id="reassignment-message" formControlName="message" name="reassignment-message" class="form-input mt-2" [dataSelector]="dataSelector + '-message-textarea'"></textarea>
      </div>

    </form>
  </div>

  <app-dialog-footer [showCancel]="false" [dataSelector]="dataSelector + '-dialog-footer'">
    <div class="w-100 d-flex justify-content-between" [dataSelector]="dataSelector + '-dialog-actions'">
      <button
        andButton
        color="default"
        type="button"
        (click)="onClose()"
        [dataSelector]="dataSelector + '-cancel-button'"
      >
        Cancel
      </button>

      <button
        andButton
        [isLoading]="isSubmitting()"
        [disabled]="isLoadingGrants"
        tooltip="Loading permissions for this email, please wait..."
        [tooltipEnabled]="isLoadingGrants"
        color="primary"
        type="button"
        (click)="savePermissions()"
        [dataSelector]="dataSelector + '-send-button'"
      >
        Send
      </button>
    </div>
  </app-dialog-footer>
</app-dialog>
